import React from 'react';
import type { ControlBridgeQueueItem } from './deriveViewModel';
import type { VgrComponentMetric } from './deriveDiagnoseHealth';
import { efficiencyPhrase } from './entropyLanguage';
import styles from './ControlBridge.module.css';

export type ControlBridgeDetailSelection =
  | { kind: 'queue'; item: ControlBridgeQueueItem }
  | { kind: 'waypoint'; item: ControlBridgeQueueItem }
  | {
      kind: 'scope';
      scopeId: string;
      label: string;
      entropy?: number | null;
      openIssues?: number;
      activeWork?: number;
      correctionFactor?: number | null;
      vgrComponents?: VgrComponentMetric[];
      healthSource?: 'manifest' | 'proxy';
    };

export interface ControlBridgeDetailPanelProps {
  selection: ControlBridgeDetailSelection | null;
  onClose: () => void;
}

export const ControlBridgeDetailPanel: React.FC<ControlBridgeDetailPanelProps> = ({
  selection,
  onClose,
}) => {
  if (!selection) return null;

  return (
    <aside className={styles.detailPanel} aria-label="Item details">
      <div className={styles.detailPanelHeader}>
        <strong>Details</strong>
        <button type="button" className={styles.detailClose} onClick={onClose} aria-label="Close details">
          ×
        </button>
      </div>
      {selection.kind === 'scope' ? (
        <div className={styles.detailBody}>
          <div className={styles.detailTitle}>{selection.label}</div>
          <div className={styles.detailMeta}>Scope: {selection.scopeId}</div>
          {selection.correctionFactor != null ? (
            <div className={styles.detailMeta}>{efficiencyPhrase(selection.correctionFactor)}</div>
          ) : null}
          {selection.entropy != null ? (
            <div className={styles.detailMeta}>Health strain index: {selection.entropy.toFixed(1)}</div>
          ) : null}
          {selection.vgrComponents?.length ? (
            <table className={styles.vgrComponentTable}>
              <thead>
                <tr>
                  <th scope="col">Component</th>
                  <th scope="col">Actual</th>
                  <th scope="col">Ideal</th>
                </tr>
              </thead>
              <tbody>
                {selection.vgrComponents.map((comp) => (
                  <tr key={comp.key}>
                    <td>{comp.label}</td>
                    <td>{comp.displayActual}</td>
                    <td>{comp.displayIdeal}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {selection.openIssues != null ? (
            <div className={styles.detailMeta}>Open issues: {selection.openIssues}</div>
          ) : null}
          {selection.activeWork != null ? (
            <div className={styles.detailMeta}>Active activities: {selection.activeWork}</div>
          ) : null}
          {selection.healthSource === 'proxy' ? (
            <div className={styles.embeddedListMeta}>VGR from proxy until backend scope-health section is live.</div>
          ) : null}
        </div>
      ) : (
        <div className={styles.detailBody}>
          <div className={styles.detailTitle}>{selection.item.title}</div>
          <div className={styles.detailMeta}>ID: {selection.item.itemId}</div>
          <div className={styles.detailMeta}>Status: {selection.item.statusPhrase}</div>
          {selection.item.priority ? (
            <div className={styles.detailMeta}>Priority: {selection.item.priority}</div>
          ) : null}
          {selection.item.issueSummary ? (
            <div className={styles.detailSummary}>{selection.item.issueSummary}</div>
          ) : null}
          {selection.item.waypointRef ? (
            <div className={styles.detailMeta}>Waypoint: {selection.item.waypointRef}</div>
          ) : null}
          {selection.item.assignedResourceLabel ? (
            <div className={styles.detailMeta}>Resource: {selection.item.assignedResourceLabel}</div>
          ) : null}
          {selection.kind === 'waypoint' ? (
            <>
              {selection.item.openImprovements != null ? (
                <div className={styles.detailMeta}>Open improvements: {selection.item.openImprovements}</div>
              ) : null}
              {selection.item.activeActivities != null ? (
                <div className={styles.detailMeta}>Active activities: {selection.item.activeActivities}</div>
              ) : null}
              {selection.item.openIssues != null ? (
                <div className={styles.detailMeta}>Open issues: {selection.item.openIssues}</div>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </aside>
  );
};
